/**
 * הסכמת עוגיות (Cookie Consent) — נוסף 27.08.2026.
 *
 * ⭐ הרקע: הוחלט (אריאנה, 27.08.2026) לחבר בעתיד Google Ads ו-Meta Ads. מערכות
 *    פרסום מציבות עוגיות ופיקסלים לא-חיוניים, שמחייבים הסכמה אקטיבית (opt-in)
 *    מהמשתמש **לפני** טעינתם — "הסכמה מעצם השימוש" אינה מספיקה לפי הנחיות
 *    הרשות להגנת הפרטיות. הרכיב הזה הוא שכבת ההסכמה.
 *
 * ⛔ הוא **אינו** טוען את מערכות הפרסום עצמן — רק שומר את בחירת המשתמש
 *    ומעדכן את אותות Google Consent Mode v2 (`gtag('consent','update',...)`).
 *    חיבור התגים בפועל (GTM / gtag / Meta Pixel) הוא שלב נפרד שיתלה בבחירה
 *    שנשמרת כאן.
 *
 * ⭐ אותו דפוס בדיוק כמו `AccessibilityContext` (4.6): `localStorage`, מיזוג
 *    שדה-שדה מול ברירת המחדל, ⛔ לעולם לא זורק. `localStorage` (⛔ לא
 *    `sessionStorage`) כי הבחירה חייבת לשרוד סגירת דפדפן.
 *
 * ⚠️ הטקסט המוצג בבאנר ובפאנל הוא ניסוח פנימי, ⛔ לא נוסח מאושר עו"ד. יש
 *    ליישר אותו עם סעיף העוגיות במדיניות הפרטיות כשהנוסח המתוקן חוזר מהחברה
 *    המלווה (ראו `דוחות/נועם_עמודים_משפטיים_תיקונים_נדרשים.docx`, פר-3/פר-4).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** ⛔ "essential" תמיד פעיל ואינו נשמר — רק שתי הקטגוריות האלה ניתנות לבחירה. */
export interface ConsentChoices {
  analytics: boolean;
  marketing: boolean;
}

export interface StoredConsent extends ConsentChoices {
  decidedAt: string;
  version: number;
}

/**
 * ⭐ העלאת המספר הזה מאלצת בקשת הסכמה מחדש מכל המשתמשים — לעשות זאת אם
 *    ספקטרום העוגיות משתנה מהותית (למשל נוספת קטגוריה שלישית).
 */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = 'noam-cookie-consent-v1';

export const DEFAULT_CHOICES: ConsentChoices = { analytics: false, marketing: false };

/** ⛔ לעולם לא זורק — כל קלט פגום מחזיר `null` ("טרם הוחלט"). */
function loadStored(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent> | null;
    if (typeof parsed !== 'object' || parsed === null) return null;
    // ⭐ גרסה שונה = מבקשים הסכמה מחדש, ⛔ לא מכבדים בחירה ישנה שלא כיסתה
    //    את מה שקיים היום.
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

/**
 * ⭐ Google Consent Mode v2 — מעדכן את אותות ההסכמה. `window.gtag` מוגדר
 *    inline ב-`index.html` (ברירת מחדל: הכל `denied`), ולכן כאן רק `update`.
 *    אם `gtag` לא קיים (בדיקות יחידה, jsdom) — no-op שקט.
 */
function applyConsentMode(choices: ConsentChoices) {
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return;
  gtag('consent', 'update', {
    analytics_storage: choices.analytics ? 'granted' : 'denied',
    ad_storage: choices.marketing ? 'granted' : 'denied',
    ad_user_data: choices.marketing ? 'granted' : 'denied',
    ad_personalization: choices.marketing ? 'granted' : 'denied',
  });
}

interface CookieConsentValue {
  /** `false` כל עוד המשתמש לא בחר — זה מה שמפעיל את הבאנר. */
  hasDecided: boolean;
  /** הבחירה האפקטיבית. עד להחלטה: הכל `false` (ברירת מחדל בטוחה). */
  choices: ConsentChoices;
  stored: StoredConsent | null;
  isManagerOpen: boolean;
  openManager: () => void;
  closeManager: () => void;
  acceptAll: () => void;
  /** עוגיות חיוניות בלבד. */
  rejectAll: () => void;
  save: (choices: ConsentChoices) => void;
}

const CookieConsentContext = createContext<CookieConsentValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredConsent | null>(() => loadStored());
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const didInitConsentMode = useRef(false);

  // ⭐ בטעינה: אם למשתמש חוזר כבר יש בחירה שמורה, מעבירים אותה מיד ל-Consent
  //    Mode כדי שתגים עתידיים יכבדו אותה עוד לפני אינטראקציה.
  if (!didInitConsentMode.current) {
    didInitConsentMode.current = true;
    if (stored) applyConsentMode(stored);
  }

  const persist = useCallback((choices: ConsentChoices) => {
    const record: StoredConsent = {
      analytics: choices.analytics === true,
      marketing: choices.marketing === true,
      decidedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    setStored(record);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // ⚠️ localStorage חסום (גלישה פרטית) — הבחירה פעילה בסשן דרך ה-state,
      //    רק לא שורדת רענון. ⛔ לא קריטי, אבל הבאנר עלול לחזור אחרי רענון.
    }
    applyConsentMode(record);
    setIsManagerOpen(false);
  }, []);

  const acceptAll = useCallback(() => persist({ analytics: true, marketing: true }), [persist]);
  const rejectAll = useCallback(() => persist({ analytics: false, marketing: false }), [persist]);

  const value = useMemo<CookieConsentValue>(
    () => ({
      hasDecided: stored !== null,
      choices: stored ?? DEFAULT_CHOICES,
      stored,
      isManagerOpen,
      openManager: () => setIsManagerOpen(true),
      closeManager: () => setIsManagerOpen(false),
      acceptAll,
      rejectAll,
      save: persist,
    }),
    [stored, isManagerOpen, acceptAll, rejectAll, persist],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent חייב לרוץ בתוך CookieConsentProvider');
  return ctx;
}
